/**
 * Keycloak roles → WPF `PermissionSnapshot`.
 * Role → permission mapping is `UserContextService.HasPermission` in LedgerVue.
 */

import type { PermissionSnapshot } from './navigationVisibility';

const ROLE_ADMIN = 'admin';
const ROLE_MANAGER = 'manager';
const ROLE_WRITER = 'writer';
const ROLE_USER = 'user';
const ROLE_VIEWER = 'viewer';

export const EMPTY_PERMISSION_SNAPSHOT: PermissionSnapshot = {
  canRead: false,
  canWrite: false,
  canReports: false,
  canAdmin: false,
};

function hasRole(roles: readonly string[], role: string): boolean {
  const wanted = role.toLowerCase();
  return roles.some((r) => r.toLowerCase() === wanted);
}

export function permissionSnapshotFromRoles(roles: readonly string[]): PermissionSnapshot {
  const isAdmin = hasRole(roles, ROLE_ADMIN);
  const isManager = hasRole(roles, ROLE_MANAGER);
  const isWriter = hasRole(roles, ROLE_WRITER);
  const isUser = hasRole(roles, ROLE_USER);
  const isViewer = hasRole(roles, ROLE_VIEWER);

  return {
    canRead: isAdmin || isManager || isWriter || isUser || isViewer,
    canWrite: isAdmin || isManager || isWriter,
    canReports: isAdmin || isManager || isUser,
    canAdmin: isAdmin,
  };
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const normalized = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    const json = decodeJwtBase64(padded);
    const parsed = JSON.parse(json) as unknown;
    if (parsed == null || typeof parsed !== 'object') return null;
    return parsed as Record<string, unknown>;
  } catch {
    return null;
  }
}

function decodeJwtBase64(padded: string): string {
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new TextDecoder().decode(bytes);
}

function rolesFromRealmAccess(value: unknown): string[] {
  if (value == null || typeof value !== 'object') return [];
  const roles = (value as { roles?: unknown }).roles;
  if (!Array.isArray(roles)) return [];
  return roles
    .filter((role): role is string => typeof role === 'string' && role.trim().length > 0)
    .map((role) => role.trim());
}

/** Extract realm roles from a Keycloak access token (`realm_access.roles`). */
export function rolesFromAccessToken(accessToken: string | null | undefined): string[] {
  if (!accessToken) return [];
  const payload = decodeJwtPayload(accessToken);
  if (!payload) return [];
  return rolesFromRealmAccess(payload.realm_access);
}

export function permissionSnapshotFromAccessToken(
  accessToken: string | null | undefined
): PermissionSnapshot {
  return permissionSnapshotFromRoles(rolesFromAccessToken(accessToken));
}
